import { debounce } from '../../../CodesThatsGottaGoSomewhereElse';
import { ObjectManager } from '../../../concepts/objects';
import { EditorModeInfo } from '../../canvas/EditorModeInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { MyRoleInfo } from '../../canvas/MyRoleInfo';
import { ToolInfo } from '../../canvas/tooling/ToolInfo';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { ActiveObject, resolve } from '../../Container';
import { Topic } from '../topics/Topic';
import { SelectedVoteRound } from './SelectedVoteRound';
import { VotingWidget } from './ui/VotingWidget';
import { Votable } from './Votable';
import { Vote, VoteAggregateAttr, VoteCountAttr, VoteRankAttr } from './Vote';
import { VoteIndex } from './VoteIndex';
import { VoteRankingInfo } from './VoteRankingInfo';
import { resolveActiveVoteRound } from './VotingHelpers';
import { votingTool } from './VotingTool';

export class VotingMechanics extends ActiveObject {

    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(VoteIndex) private voteIndex!: VoteIndex;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    public start(): void {
        this.burden(

            this.objects.on('VoteRound', 'allocated', debounce(this.updateVoteAttributesForAll)), // debounce to allow props to be set
            this.objects.on('VoteRound', 'released', this.updateVoteAttributesForAll),
            this.session.observe(SelectedVoteRound, this.updateVoteAttributesForAll),
            this.session.observe(VoteRankingInfo, this.onVoteRankingInfoChanged),
            this.voteIndex.on('updated', this.updateVoteAttributesForAll),
            // TODO: We need to be able to tell if the "cousins" of a topic have changed to effeciently fire this, but we can't until we
            // update podopollis to give more information on a commit so for now just debounce to stop spam
            this.objects.on('Topic', 'affected', debounce(this.updateVoteAttributesForAll, 50)),
            this.session.observe(MyRoleInfo, this.syncVotingWidget),
            this.session.observe(EditorModeInfo, this.syncVotingWidget),
            this.session.observe(SelectedVoteRound, this.syncVotingWidget),
            this.session.observe(ToolInfo, this.syncVotingWidget),
            this.voteIndex.on('updated', this.syncVotingWidget),
        );
        this.updateVoteAttributesForAll();
        this.syncVotingWidget();
    }

    private updateVoteAttributesForAll = () => {

        const subjects = this.objects.getAll(Votable);
        const rankingsInfo = this.session.get(VoteRankingInfo);
        const objectRankings = new VoteRankings();
        const topicRankings = new VoteRankings();

        const activeRound = resolveActiveVoteRound(this.objects, this.session);
        const countFor = (id: string) => !!activeRound ? this.voteIndex.count(activeRound.id, id) : 0;

        for (const elmt of subjects) {
            const count = countFor(elmt.id);

            if (elmt.is(Topic)) {
                let aggr = countFor(elmt.id) ?? 0;
                for (const sc of elmt.as(Topic).cousins()) {
                    if (sc.is(Votable)) {
                        aggr += countFor(sc.id);
                    }
                }
                VoteAggregateAttr.setMaybe(elmt, aggr || null);
                topicRankings.add(elmt.id, aggr);
            }
            else {
                objectRankings.add(elmt.id, count);
            }

            VoteCountAttr.setMaybe(elmt, count || null);
        }

        if (rankingsInfo.enabled) {
            const rankings = rankingsInfo.mode === 'object' ? objectRankings : topicRankings;
            for (const elmt of subjects) {
                VoteRankAttr.setMaybe(elmt, rankings.get(elmt.id));
            }
        }

    };

    private removeRankingAttributesForAll = () => {

        const subjects = this.objects.getAll(Votable);
        for (const elmt of subjects) {
            VoteRankAttr.setMaybe(elmt, null);
        }
    };

    private onVoteRankingInfoChanged = (next: VoteRankingInfo) => {
        this.removeRankingAttributesForAll();
        if (next.enabled) {
            this.updateVoteAttributesForAll();
        }
    };

    private syncVotingWidget = () => {

        const { canParticipate } = this.session.get(MyRoleInfo);
        const toolInfo = this.session.get(ToolInfo);
        const votesExist = this.objects.getAll(Vote).size;

        if (canParticipate && votesExist || toolInfo.id === votingTool.id) {
            this.realizeWidget();
        }
        else {
            this.destroyWidget();
        }
    };

    private realizeWidget() {
        const { widgets } = this;
        if (widgets.get('voting')) return;

        const wc = widgets.createContainer();
        wc.name = 'voting';
        wc.position = ['docked', 'bottomLeft', 'stack'];
        wc.direction = 'row';
        wc.add(widgets.createInline({ ui: VotingWidget }));

        widgets.add(wc);
    }

    private destroyWidget() {
        const { widgets } = this;
        const wc = widgets.get('voting');
        if (wc) {
            widgets.remove(wc);
        }
    }
}

class VoteRankings {

    private dirty = false;
    private counts = new Map<string, number>();
    private rankList = [] as string[];
    private rankIndex = new Map<string, number>();

    public add(id: string, count: number) {
        if (count <= 0) return;
        this.counts.set(id, count);
        this.rankList.push(id);
        this.dirty = true;
    }

    public get(id: string): number {
        this.recalculate();
        return this.rankIndex.get(id) ?? null;
    }

    private recalculate() {
        if (this.dirty) {
            this.rankIndex.clear();
            this.rankList.sort((a, b) => this.counts.get(b) - this.counts.get(a));
            this.rankList.forEach((x, i) => this.rankIndex.set(x, i + 1));
        }
        this.dirty = false;
    }
}
